import Component from '@glimmer/component'
import { service } from '@ember/service'
import SubmitFormRunner from 'teacher-ui-client/utils/submit-form-runner'
import { Changeset } from 'ember-changeset'
import { editClassValidator } from 'teacher-ui-client/school-class/validator'
import lookupValidator from 'teacher-ui-client/utils/lookup-validator'
import { isConflictError } from 'teacher-ui-client/resource-conflict/error'
import { tracked } from '@glimmer/tracking'
import type { IntlService } from 'ember-intl'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type { Log } from '@blakeelearning/log'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import type Teacher from 'teacher-ui-client/models/teacher'
import { FormField } from 'teacher-ui-client/components/form-field'
import {
  FormWrapperSubmitButton,
  type ExternalSubmitState,
} from 'teacher-ui-client/components/form-wrapper/submit-button'
import { or } from 'ember-truth-helpers'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { t } from 'ember-intl'
import type Owner from '@ember/owner'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface Args {
  teachers: Teacher[]
  createSchoolClassAction: (name: string, teacherId: string) => unknown
  showChooseTeacherControl: boolean
}

interface Signature {
  Element: HTMLFormElement
  Args: Args
}

export class CreateSchoolClass extends Component<Signature> {
  @service intl!: IntlService
  @service flashQueue!: FlashQueueService
  @service log!: Log
  @service resourceConflict!: ResourceConflictService

  @tracked changeset

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    const data = { name: '', teacher: '' }
    this.changeset = Changeset(data, lookupValidator(editClassValidator, { intl: this.intl }), editClassValidator)
  }

  get teachersAsOptions() {
    const options = this.args.teachers.filter(hasId).map((teacher) => {
      const { fullName: text, id: uid } = teacher
      return {
        text,
        value: uid,
        uid,
        teacher,
      }
    })
    return options.sort((a, b) => a.text.localeCompare(b.text))
  }

  onSuccess(name: string) {
    const message = this.intl.t('manage.schoolClasses.createSuccess', { name })
    this.flashQueue.addSuccess({ subtitle: message })
    this.changeset.rollback()
  }

  onError(schoolClassName: string, teacherId: string, teacherName: string, errors?: unknown[]) {
    if (errors && errors.length > 0 && isConflictError(errors[0])) {
      return this.resourceConflict.handleError('handle create school class', errors[0])
    }

    const message = this.intl.t('manage.schoolClasses.createError', { name: schoolClassName })
    this.flashQueue.addFail({ subtitle: message })

    this.log.error(
      `Error creating a new school class: '${schoolClassName}', teacher: ${teacherName} (${teacherId})`,
      errors,
    )
  }

  @tracked submitState: ExternalSubmitState = 'default'

  submit = (event: Event) => {
    event.preventDefault()
    const name = this.changeset.get('name') as string
    const teacher = this.changeset.get('teacher') as { uid: string; text: string }
    const teacherId = teacher.uid
    const teacherName = teacher.text

    this.submitState = 'default'

    const submitFormRunner = new SubmitFormRunner(this, 'submitState')
    submitFormRunner.success(() => this.onSuccess(name))
    submitFormRunner.fail((_error: unknown, errors?: unknown[]) => this.onError(name, teacherId, teacherName, errors))
    void submitFormRunner.run(() => this.args.createSchoolClassAction(name, teacherId))
  }

  <template>
    <form class="flex flex-row flex-wrap gap-2" ...attributes>
      <FormField
        class="min-w-40 shrink-0 grow basis-0"
        data-test-field="add-schoolclass-name"
        @changeset={{this.changeset}}
        @labelText={{t "manage.manageClasses.classes.name"}}
        @propertyName="name"
        as |field|
      >
        <field.input placeholder={{field.labelText}} aria-label={{field.labelText}} />
      </FormField>
      {{#if @showChooseTeacherControl}}
        <FormField
          class="min-w-40 shrink-0 grow basis-0"
          @changeset={{this.changeset}}
          @labelText={{t "manage.schoolClasses.selectTeacher"}}
          @propertyName="teacher"
          as |field|
        >
          <field.select
            data-test-select-teacher
            aria-label={{field.labelText}}
            @options={{this.teachersAsOptions}}
            @defaultText={{t "manage.schoolClasses.selectTeacher"}}
          />
        </FormField>
      {{/if}}
      <FormWrapperSubmitButton
        data-test-add-school-class-submit
        @type="button"
        @disabled={{or this.changeset.isInvalid this.changeset.isPristine}}
        @defaultText={{t "manage.singularActions.submitGo"}}
        @state={{this.submitState}}
        @appearance={{ButtonAppearance.regular}}
        @clickAction={{this.submit}}
      />
    </form>
  </template>
}

export default CreateSchoolClass
